import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, ClipboardPaste, Loader2, ArrowRight, UploadCloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";

function summarise(kind, f) {
  if (kind === "load") {
    const route = [f.origin, f.destination].filter(Boolean).join(" → ");
    const spec = [f.material_type, f.weight && `${f.weight}T`, f.truck_type, f.pickup_date].filter(Boolean).join(" · ");
    return { route: route || "Load", spec };
  }
  const route = [f.current_location, f.destination].filter(Boolean).join(" → ");
  const spec = [f.truck_type, f.capacity && `${f.capacity}T`, f.available_date].filter(Boolean).join(" · ");
  return { route: route || "Truck", spec };
}

export function PasteAutoFill({ kind, onFilled, onPostAll }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [posting, setPosting] = useState(false);
  const [matches, setMatches] = useState([]);

  const reset = () => { setOpen(false); setText(""); setMatches([]); };

  const fill = (fields) => {
    const filled = Object.values(fields).filter((v) => v !== "").length;
    onFilled(fields);
    toast.success(`Auto-filled ${filled} field${filled > 1 ? "s" : ""} — please review before posting.`);
    reset();
  };

  const postAll = async () => {
    if (!onPostAll || !matches.length) return;
    setPosting(true);
    try {
      await onPostAll(matches);
      reset();
    } catch (e) {
      /* parent surfaces the toast */
    } finally { setPosting(false); }
  };

  const run = async () => {
    if (!text.trim()) { toast.error("Paste a WhatsApp message first."); return; }
    setBusy(true);
    setMatches([]);
    try {
      const { data } = await api.post("/parse-post", { text, kind });
      const items = (data.items && data.items.length ? data.items : (data.fields ? [data.fields] : []));
      if (!items.length) { toast.message("Couldn't find any details — please fill the form manually."); return; }
      if (items.length === 1) { fill(items[0]); return; }
      setMatches(items);
      toast.success(`Found ${items.length} ${kind === "load" ? "loads" : "trucks"} — post them all or tap one to fill the form.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read that message.");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button
        type="button"
        data-testid={`paste-autofill-toggle-${kind}`}
        onClick={() => setOpen(true)}
        className="w-full flex items-center gap-2 rounded-xl border border-dashed border-primary/40 bg-primary/5 px-3 py-2.5 text-sm text-primary hover:bg-primary/10 transition-colors"
      >
        <Sparkles className="w-4 h-4 shrink-0" />
        <span className="font-medium">Paste a WhatsApp message to auto-fill</span>
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 space-y-2">
      <div className="flex items-center gap-2 text-sm text-primary font-medium">
        <Sparkles className="w-4 h-4" /> Paste &amp; auto-fill
      </div>
      <Textarea
        data-testid={`paste-autofill-text-${kind}`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={4}
        placeholder={kind === "load"
          ? "Paste one or many loads, e.g. Pune to Delhi 9T frozen -18/4 32ft tomorrow. You can paste a full group message with several loads."
          : "Paste one or many trucks, e.g. 32ft reefer at Nashik → Delhi 15T -25/20 tomorrow. You can paste a full group message with several trucks."}
        className="text-sm"
      />

      {matches.length > 1 && (
        <div data-testid={`paste-autofill-matches-${kind}`} className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">Found {matches.length} — post all, or tap one to fill:</p>
            {onPostAll && (
              <Button
                type="button"
                data-testid={`paste-autofill-postall-${kind}`}
                onClick={postAll}
                disabled={posting || busy}
                size="sm"
                className="rounded-full h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {posting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5 mr-1" />}
                {posting ? "Posting…" : `Post all ${matches.length}`}
              </Button>
            )}
          </div>
          <div className="max-h-52 overflow-y-auto space-y-1.5 pr-0.5">
            {matches.map((m, i) => {
              const s = summarise(kind, m);
              return (
                <button
                  key={`${s.route}-${s.spec}-${i}`}
                  type="button"
                  data-testid={`paste-autofill-match-${kind}-${i}`}
                  onClick={() => fill(m)}
                  disabled={posting}
                  className="w-full text-left rounded-lg border border-border bg-background hover:border-primary/50 hover:bg-primary/5 px-3 py-2 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate">{s.route}</p>
                    {s.spec && <p className="text-xs text-muted-foreground truncate font-mono-num">{s.spec}</p>}
                  </div>
                  <ArrowRight className="w-4 h-4 text-primary shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button
          type="button"
          data-testid={`paste-autofill-run-${kind}`}
          onClick={run}
          disabled={busy}
          size="sm"
          className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
        >
          {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ClipboardPaste className="w-4 h-4 mr-1.5" />}
          {busy ? "Reading…" : matches.length ? "Read again" : "Auto-fill form"}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={reset} className="rounded-full">
          Cancel
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">AI reads your message and fills the fields below — always review before posting.</p>
    </div>
  );
}
